// SPDX-License-Identifier: BUSL-1.1

/**
 * Invitation acceptance page — /invite/:token
 *
 * Allows a signed-in user to accept an organization invitation.
 * This is a public route (not behind RequireAuth) because the user
 * needs to be able to land here and sign in with the invited email.
 *
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useAuth, fetchAndStoreBearerJwt, setActiveOrganization } from '@/lib/auth';
import { API_BASE } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { showSuccess } from '@/lib/toast';

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Small delay to allow auth state to settle
    const timer = setTimeout(() => setChecking(false), 200);
    return () => clearTimeout(timer);
  }, []);

  async function handleAccept() {
    if (!token) return;
    setAccepting(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/invitations/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const organizationId = typeof data.organizationId === 'string' ? data.organizationId : null;
        if (organizationId) {
          const result = await setActiveOrganization(organizationId);
          if (!result.ok) {
            const message =
              result.reason === 'unavailable'
                ? 'Your invitation was accepted, but workspace activation is not available yet. Please sign in again.'
                : result.message || 'Failed to activate your workspace after accepting the invitation.';
            setError(message);
            return;
          }

          const refreshedToken = await fetchAndStoreBearerJwt({ forceRefresh: true });
          if (!refreshedToken) {
            setError('Your invitation was accepted, but the workspace session could not be refreshed. Please sign in again.');
            return;
          }
        }
        showSuccess('Welcome! You now have access to the organization.');
        navigate('/');
      } else {
        setError(data.error ?? 'Failed to accept invitation. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setAccepting(false);
    }
  }

  if (checking || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-700 border-t-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-950">
        <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
          <div className="mb-6 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-900/30">
              <svg
                className="h-6 w-6 text-emerald-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <h1 className="mb-2 text-lg font-semibold text-neutral-100">
            You've been invited
          </h1>
          <p className="mb-6 text-sm text-neutral-500">
            Sign in with the email address that received this invitation to join
            the organization.
          </p>

          <Link to="/sign-in">
            <Button className="w-full bg-emerald-600 hover:bg-emerald-500">
              Sign in
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950">
      <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
        <div className="mb-6 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-900/30">
            <svg
              className="h-6 w-6 text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
          </div>
        </div>

        <h1 className="mb-2 text-lg font-semibold text-neutral-100">
          Accept invitation
        </h1>
        <p className="mb-6 text-sm text-neutral-500">
          Click below to join this organization. You will gain access based on
          the role assigned by the administrator.
        </p>

        {error && (
          <div className="mb-4 rounded border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <Button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
        >
          {accepting ? 'Accepting...' : 'Accept invitation'}
        </Button>
      </div>
    </div>
  );
}
